import { useEffect, useState, useMemo } from 'react';
import { Outlet, Route, Routes, useNavigate, useParams } from 'react-router';
import * as Sentry from '@sentry/react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { InfoCircledIcon } from '@radix-ui/react-icons';

import { ProjectRootScreen } from './pages/project-root.screen';
import { PageUnauthorized } from '../../components/unauthorized-placeholder/unauthorized-placeholder';
import { VideoComparePage } from './video-compare.page';
import { tabPool } from '../../features/tabs/models/tabs-pool';
import { uiStore } from '../../core/stores/ui-store';
import { CrashErrorScreen } from './components/crash-error-screen';
import { ProjectSettingsRoute } from './project-settings.route';
import { ProjectCalendarRoute } from './project-calendar.route';
import { useCurrentProject } from '../../hooks/use-current-project';
import { breakPoint } from '../../app/theme';
import { ProjectMenu } from './components/project-menu';
import { bootstrapProject, unarchiveProject } from '../../features/projects/store/projects.slice';
import { PageLoader } from '../../app/components/page-loader';
import { Task, Member } from '../../app/entities';
import { useCurrentMember } from '../../hooks/use-current-member';
import { formatDate } from '../../lib/utils/DateHelper';
import { catchError } from '../../core/catch-error';
import { ElevatedButton } from '@producer/ui-kit';

export const ProjectPage = observer(() => {
  const { projectId } = useParams() as { projectId: string };
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  const project = useCurrentProject();
  const currentMember = useCurrentMember();

  /**
   * TODO URGENT
   *
   * How do we handle unauthorized errors ?
   */

  useEffect(() => {
    uiStore.isDataFetching = true;

    bootstrapProject(projectId!).then(() => {
      setIsLoading(false);
      Task.fetchByProject(projectId).then(() => (uiStore.isDataFetching = false));
    });
  }, [projectId]);

  // Check if user can unarchive
  const canUnarchive = useMemo(() => {
    if (!currentMember || !project) return false;
    if (currentMember.role === 'guest') return false;
    if (['owner', 'admin'].includes(currentMember.role)) return true;
    return project.createdBy === currentMember.userId;
  }, [currentMember, project]);

  const handleUnarchive = async () => {
    if (project) {
      try {
        await unarchiveProject(project._id);
      } catch (e) {
        catchError(e);
      }
    }
  };

  const archivedByMember = useMemo(() => {
    if (!project?.archivedById) return null;
    return Member.getOne(project.archivedById);
  }, [project?.archivedById]);

  useEffect(() => {
    if (project) {
      uiStore.pageTitle = {
        label: project.name,
        href: `/space/${project.spaceId}/projects/${project._id}`,
      };

      uiStore.showBack = true;
      uiStore.goBack = () => navigate('../');
    }

    tabPool.updateTab({
      key: projectId,
      label: project?.name,
    });

    return () => {
      uiStore.pageTitle = undefined;
      uiStore.showBack = false;
      uiStore.goBack = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, projectId]);

  return (
    <Sentry.ErrorBoundary fallback={<CrashErrorScreen />}>
      <Container>
        <ProjectMenu isOpen={isDrawerOpen} setIsOpen={setIsDrawerOpen} />

        <Content data-expand={isDrawerOpen}>
          {isLoading ? (
            <PageLoader />
          ) : (
            <>
              {project?.archived && (
                <ArchivedBanner>
                  <BannerContent>
                    <InfoCircledIcon />
                    <span>
                      This project was archived on{' '}
                      {project.archivedAt && formatDate(project.archivedAt.toString(), 'MMM dd, yyyy')}{' '}
                      {archivedByMember && `by ${archivedByMember.fullName}`}
                    </span>
                  </BannerContent>
                  {canUnarchive && (
                    <ElevatedButton text="Unarchive" onClick={handleUnarchive} />
                  )}
                </ArchivedBanner>
              )}

              <Outlet />

              {project ? (
                <Routes>
                  <Route
                    path="/steps/:stepId/assets/:assetId/compare-versions"
                    element={<VideoComparePage />}
                  />
                  <Route path="/settings" element={<ProjectSettingsRoute project={project} />} />
                  <Route path="/calendar" element={<ProjectCalendarRoute project={project} />} />
                  <Route path="/" element={<ProjectRootScreen />} />
                </Routes>
              ) : (
                <PageUnauthorized />
              )}
            </>
          )}
        </Content>
      </Container>
    </Sentry.ErrorBoundary>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;

  & > div:first-child {
    display: none;
  }

  @media screen and (min-width: ${breakPoint.medium - 1}px) {
    & > div:first-child {
      display: flex;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: calc(100%);
  overflow: hidden;
  transition: all 0.2s ease-in;

  @media screen and (min-width: ${breakPoint.medium}px) {
    &[data-expand='true'] {
      max-width: calc(100% - 200px);
    }
  }
`;

export const Header = styled.div`
  height: 4rem;
  min-height: 4rem;
  width: 100%;
  border-bottom: 1px var(--content-border-color) solid;
  color: #e3e4e7;
  font-size: 1.4rem;
  font-weight: 600;
  padding: 0 1.6rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  svg {
    height: 1.6rem;
    width: 1.6rem;
  }
`;

const ArchivedBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.2rem 1.6rem;
  background-color: rgba(255, 165, 0, 0.1);
  border-bottom: 1px solid rgba(255, 165, 0, 0.3);
  color: var(--color-grayscale-white);
  font-size: 1.4rem;
  gap: 1.6rem;
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;

  svg {
    width: 1.6rem;
    height: 1.6rem;
    color: #ffa500;
  }
`;
